// @react
import React, { ReactElement } from 'react'
// @libraries
import useResizeObserver from 'use-resize-observer'
import clsx from 'clsx'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
// @components
import Grid from '@bit/atd.web.grid'
import colors from '@bit/atd.web.colors'
// @card parts
import Title from '../../../Parts/Title'
import TextLink from '@bit/atd.web.text-link'
import Icon from '@bit/atd.web.icons'

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 0',
    '& ~ $wrap': {
      borderTop: `1px solid ${colors.gray300}`,
    },
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  topContainer: {
    flexWrap: 'nowrap',
  },
  bottomContainer: {
    marginTop: '16px',
  },
  imageItem: {
    width: '80px',
    flexShrink: 0,
    '& + div': {
      marginLeft: '16px',
    },
  },
  contentItem: {
    flexGrow: 1,
    '& + div': {
      marginLeft: '16px',
    },
  },
  priceItem: {
    marginLeft: 'auto',
  },
  toggleItem: {},
  image: {
    width: '100%',
    height: '110px',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  title: {
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '22px',
    marginBottom: '8px',
  },
  description: {
    color: '#424140',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    marginBottom: '16px',
  },
  category: {
    color: '#7c7c7c',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '16px',
    marginTop: 'auto',
    textTransform: 'uppercase',
  },
  price: {
    marginLeft: 'auto',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '22px',
    color: colors.textActive,
  },
  isHorizontal: {
    '& $title': {
      fontSize: '20px',
      lineHeight: '24px',
    },
  },
  downLoadLink: {
    display: 'flex',
    alignItems: 'center',
    '& span': {
      marginRight: '5px',
    },
  },
})

export type PropsType = {
  url?: string
  downloadURL?: string
  orderLineNumber?: number
  imageURL?: string
  title: string
  titleURL?: string
  description?: string
  category?: string
  price?: number
  type?: string
  autoRenew?: boolean
  onAutoRenewChange?: () => void
}

const LineItem = ({
  url,
  imageURL,
  downloadURL,
  title,
  titleURL,
  description,
  category,
  type,
  price,
}: // autoRenew, @todo the backend is not going to be able to deliver this in the end. we need to do additional work to fetch membership and match to the auto-renew status, but this has been moved to backlog*
// onAutoRenewChange, @todo the backend is not going to be able to deliver this in the end. we need to do additional work to fetch membership and match to the auto-renew status, but this has been moved to backlog*
PropsType): ReactElement => {
  /****************************************
   *  HOOKS
   ****************************************/

  const classes = useStyles()

  const { ref, width } = useResizeObserver<HTMLDivElement>()
  const watchCardWidth = width && width > 460

  let isHorizontal = false
  if (watchCardWidth) isHorizontal = true

  /****************************************
   *  RENDERS
   ****************************************/

  const itemTitle = () => {
    if (url) {
      return (
        <TextLink href={url} className={classes.title}>
          <Title title={title} titleURL={titleURL} />
        </TextLink>
      )
    }
    return (
      <Typography className={classes.title}>
        <Title title={title} titleURL={titleURL} />
      </Typography>
    )
  }

  return (
    <div
      className={clsx(classes.wrap, {
        [classes.isHorizontal]: isHorizontal,
      })}
      ref={ref}
    >
      {/* Top Container */}
      <Grid container className={classes.topContainer}>
        {/* Image */}
        {imageURL && (
          <Grid item xs={'auto'} className={classes.imageItem}>
            <span className={classes.image}>
              <img src={imageURL} alt={title || ''} />
            </span>
          </Grid>
        )}

        {/* Content */}
        <Grid item xs={'auto'} className={classes.contentItem}>
          {itemTitle()}

          {downloadURL && (
            <div>
              <TextLink href={downloadURL} className={classes.downLoadLink}>
                <span>Download</span>
                <Icon glyph="download" size={16} color={colors.ctaPrimary} />
              </TextLink>
            </div>
          )}

          {description && (
            <Typography className={classes.description}>
              {description}
            </Typography>
          )}

          {!isHorizontal && (
            <>
              {category && (
                <Typography className={classes.category}>{category}</Typography>
              )}
            </>
          )}
        </Grid>

        {/* Price */}
        <Grid item xs={'auto'} className={classes.priceItem}>
          <span className={classes.price}>${price && price.toFixed(2)}</span>
        </Grid>
      </Grid>

      {/* Bottom Container */}
      {(category || type === 'MBR') && (
        <Grid container className={classes.bottomContainer}>
          {isHorizontal && (
            <>
              <Grid item xs={'auto'} className={classes.imageItem} />

              <Grid
                item
                xs={'auto'}
                className={classes.contentItem}
                style={{ alignSelf: 'flex-end' }}
              >
                {category && (
                  <Typography className={classes.category}>
                    {category}
                  </Typography>
                )}
              </Grid>
            </>
          )}

          {/*@todo the backend is not going to be able to deliver this in the end. we need to do additional work to fetch membership and match to the auto-renew status, but this has been moved to backlog*/}
          {/*{type === 'MBR' && (*/}
          {/*  <Grid item xs={'auto'} className={classes.toggleItem}>*/}
          {/*    <Toggle*/}
          {/*      onOff*/}
          {/*      name="1"*/}
          {/*      label="Auto-Renew"*/}
          {/*      labelPlacement={'default'}*/}
          {/*      size={'small'}*/}
          {/*      checked={autoRenew}*/}
          {/*      onChange={() => onAutoRenewChange && onAutoRenewChange()}*/}
          {/*    />*/}
          {/*  </Grid>*/}
          {/*)}*/}
        </Grid>
      )}
    </div>
  )
}

export default LineItem
